import { useRef, type ReactNode, type CSSProperties } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  type HTMLMotionProps,
} from "framer-motion";

interface TiltCardProps extends Omit<HTMLMotionProps<"div">, "style"> {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Max tilt in degrees. Default 14 */
  intensity?: number;
  /** Whether to show the moving gold glare. Default true */
  glare?: boolean;
}

export function TiltCard({
  children,
  className = "",
  style,
  intensity = 14,
  glare = true,
  onMouseEnter,
  onMouseLeave,
  onClick,
  ...rest
}: TiltCardProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Raw tilt values
  const rawRotX = useMotionValue(0);
  const rawRotY = useMotionValue(0);
  const rawScale = useMotionValue(1);

  // Glare origin (percentage)
  const glareX = useMotionValue(50);
  const glareY = useMotionValue(50);

  // Spring-smoothed versions
  const rotateX = useSpring(rawRotX, { stiffness: 260, damping: 22 });
  const rotateY = useSpring(rawRotY, { stiffness: 260, damping: 22 });
  const scale   = useSpring(rawScale, { stiffness: 300, damping: 28 });
  const glareOp = useSpring(0, { stiffness: 260, damping: 22 });

  const glareBackground = useTransform(
    [glareX, glareY],
    ([x, y]) =>
      `radial-gradient(circle at ${x}% ${y}%, oklch(0.95 0.12 85 / 18%) 0%, transparent 55%)`
  );

  function calcTilt(clientX: number, clientY: number) {
    const el = ref.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    const dx = (clientX - left - width  / 2) / (width  / 2); // -1 → +1
    const dy = (clientY - top  - height / 2) / (height / 2); // -1 → +1
    rawRotX.set(-dy * intensity);
    rawRotY.set( dx * intensity);
    glareX.set(((clientX - left) / width)  * 100);
    glareY.set(((clientY - top)  / height) * 100);
  }

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    calcTilt(e.clientX, e.clientY);
  }

  function handleMouseEnter(e: React.MouseEvent<HTMLDivElement>) {
    rawScale.set(1.03);
    glareOp.set(1);
    onMouseEnter?.(e);
  }

  function handleMouseLeave(e: React.MouseEvent<HTMLDivElement>) {
    rawRotX.set(0);
    rawRotY.set(0);
    rawScale.set(1);
    glareOp.set(0);
    onMouseLeave?.(e);
  }

  function handleTouchMove(e: React.TouchEvent<HTMLDivElement>) {
    const t = e.touches[0];
    if (!t) return;
    calcTilt(t.clientX, t.clientY);
    rawScale.set(1.03);
    glareOp.set(1);
  }

  function handleTouchEnd() {
    rawRotX.set(0);
    rawRotY.set(0);
    rawScale.set(1);
    glareOp.set(0);
  }

  return (
    <motion.div
      ref={ref}
      className={`relative ${className}`}
      style={{
        ...style,
        rotateX,
        rotateY,
        scale,
        transformStyle: "preserve-3d",
        transformPerspective: 900,
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onClick={onClick}
      {...rest}
    >
      {/* Card content sits at z=0 */}
      <div style={{ transform: "translateZ(0px)" }}>{children}</div>

      {/* Gold glare highlight */}
      {glare && (
        <motion.div
          className="absolute inset-0 rounded-[inherit] pointer-events-none z-20"
          style={{ opacity: glareOp, background: glareBackground }}
        />
      )}

      {/* Subtle gold bottom-edge glow when tilted */}
      <motion.div
        className="absolute inset-x-0 bottom-0 h-[2px] rounded-b-[inherit] pointer-events-none z-20"
        style={{
          opacity: useTransform(glareOp, [0, 1], [0, 0.7]),
          background: "linear-gradient(90deg, transparent, oklch(0.78 0.15 78 / 60%), transparent)",
        }}
      />
    </motion.div>
  );
}
